import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Flex,
    Heading,
    Text,
    VStack,
    HStack,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    useDisclosure,
    useColorModeValue,
    Spinner,
    useToast,
    Badge,
    Divider,
    Card,
    CardBody,
    CardHeader,
    Progress,
    Tooltip,
    Container,
    useBreakpointValue,
    Stack,
    Collapse,
    ScaleFade,
    SlideFade,
    Image,
    SimpleGrid,
    Avatar,
    Center,
} from '@chakra-ui/react';
import { FaTrash, FaVideo, FaPaperclip, FaArrowUp, FaArrowDown, FaPlay, FaClock, FaEye, FaChevronRight, FaGraduationCap, FaBookOpen } from 'react-icons/fa';
import { FiLock, FiUnlock } from "react-icons/fi";
import { AddIcon, DeleteIcon, HamburgerIcon } from '@chakra-ui/icons';
import axiosInstance from '../services/axiosInstance';
import { useParams } from 'react-router-dom';
import { deleteVideo, fetchCourseById, toggleChaptire } from '../services/courseService';
import VideoPlayer from '../shared/videoplayer';
import NewChapterToCourse from '../shared/newchapitreCreation';
import AddPartieForm from '../shared/AddPartieForm';
import { urlvideo } from '../services/urls';

const CourseDetailsContent = () => {
    const { id } = useParams();
    const [courseData, setCourseData] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [videoUploadModalOpen, setVideoUploadModalOpen] = useState(false);
    const [selectedChapterIndex, setSelectedChapterIndex] = useState(null);
    const [selectedPartieIndex, setSelectedPartieIndex] = useState(null);
    const [ChaterID, setChaterID] = useState(null);
    const [newVideo, setNewVideo] = useState({
        name: '',
        description: '',
        duree: '',
        file: null,
    });
    const [isCalculatingDuration, setIsCalculatingDuration] = useState(false);
    const [uploadAnimation, setUploadAnimation] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [attachments, setAttachments] = useState([]);
    const [AttchmentName, setAttchmentName] = useState('');
    const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isAddNewPartieOpen, setisAddNewPartieOpen] = useState(false);
    const [expandedChapters, setExpandedChapters] = useState(new Set([0]));
    const [expandedParties, setExpandedParties] = useState(new Set());
    const toast = useToast();

    // Homework replies state
    const [allHomeworkReplies, setAllHomeworkReplies] = useState([]);
    const [isLoadingHomework, setIsLoadingHomework] = useState(false);
    const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
    const [isSendingSms, setIsSendingSms] = useState(false);

    const fetchHomeworkReplies = async (video) => {
        if (!video?._id || !courseData?._id) return;
        setIsLoadingHomework(true);
        try {
            const res = await axiosInstance.get(`/video-homework/${video._id}/replies`, {
                params: { courseId: courseData._id }
            });
            setAllHomeworkReplies(res.data.data || []);
        } catch (err) {
            console.error('Error fetching homework replies:', err);
        } finally {
            setIsLoadingHomework(false);
        }
    };

    const handleSendSms = async (sendTo) => {
        if (!selectedVideo || !courseData) return;
        setIsSendingSms(true);
        try {
            const res = await axiosInstance.post('/video-homework/send-sms', {
                videoId: selectedVideo._id,
                courseId: courseData._id,
                videoName: selectedVideo.nomvideo || 'فيديو',
                sendTo,
            });
            toast({
                title: res.data.message,
                status: res.data.success ? 'success' : 'warning',
                duration: 4000,
                isClosable: true,
                position: 'top-left',
            });
            setIsSmsModalOpen(false);
        } catch (err) {
            toast({
                title: 'خطأ في الإرسال',
                description: err.response?.data?.message || 'فشل في إرسال الرسائل',
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top-left',
            });
        } finally {
            setIsSendingSms(false);
        }
    };

    // Responsive breakpoints
    const isMobile = useBreakpointValue({ base: true, md: false });
    const sidebarWidth = useBreakpointValue({ base: "0", md: "200px", lg: "250px" });
    const containerPadding = useBreakpointValue({ base: 4, md: 6, lg: 8 });
    const cardSpacing = useBreakpointValue({ base: 4, md: 6, lg: 8 });
    const headerSize = useBreakpointValue({ base: "lg", md: "xl", lg: "2xl" });
    const buttonSize = useBreakpointValue({ base: "sm", md: "md", lg: "lg" });
    const modalSize = useBreakpointValue({ base: "full", md: "xl", lg: "2xl" });

    // Professional, clean color palette
    const bgColor = useColorModeValue('gray.50', 'gray.900');
    const cardBg = useColorModeValue('white', 'gray.800');
    const sectionHeaderBg = useColorModeValue('gray.100', 'gray.700');
    const sectionHeaderHoverBg = useColorModeValue('gray.200', 'gray.600');
    const partieHeaderBg = useColorModeValue('gray.50', 'gray.800');
    const partieHeaderHoverBg = useColorModeValue('gray.100', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const textColor = useColorModeValue('gray.800', 'gray.100');
    const accentColor = useColorModeValue('blue.600', 'blue.300');
    const accentBg = useColorModeValue('blue.50', 'blue.900');
    const lockRed = useColorModeValue('red.500', 'red.400');
    const lockGreen = useColorModeValue('green.500', 'green.400');

    const fetchCourseData = async () => {
        try {
            const response = await fetchCourseById(id);
            setCourseData(response.data);
            if (response.data.chapitres?.length > 0) {
                const firstChapter = response.data.chapitres[0];
                if (firstChapter.parties?.length > 0 && firstChapter.parties[0].videos?.length > 0) {
                    setSelectedVideo(firstChapter.parties[0].videos[0]);
                }
            }
        } catch (error) {
            toast({
                title: "خطأ",
                description: "فشل تحميل بيانات الدورة",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-left"
            });
        }
    };

    // Function to calculate video duration
    const calculateVideoDuration = (file) => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                window.URL.revokeObjectURL(video.src);
                const duration = video.duration;
                const minutes = Math.floor(duration / 60);
                const seconds = Math.floor(duration % 60);
                const formattedDuration = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                resolve(formattedDuration);
            };
            video.onerror = () => {
                window.URL.revokeObjectURL(video.src);
                reject(new Error('Error loading video'));
            };
            video.src = URL.createObjectURL(file);
        });
    };

    const handleVideoFileChange = async (file) => {
        if (file) {
            setIsCalculatingDuration(true);
            try {
                const duration = await calculateVideoDuration(file);
                setNewVideo(prev => ({
                    ...prev,
                    file: file,
                    duree: duration,
                    name: prev.name || file.name.replace(/\.[^/.]+$/, "")
                }));
                toast({
                    title: "تم حساب المدة",
                    description: `مدة الفيديو: ${duration}`,
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                    position: "top-left"
                });
            } catch (error) {
                setNewVideo(prev => ({
                    ...prev,
                    file: file,
                    name: prev.name || file.name.replace(/\.[^/.]+$/, "")
                }));
                toast({
                    title: "تحذير",
                    description: "لم يتم حساب مدة الفيديو تلقائياً، يرجى إدخالها يدوياً",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                    position: "top-left"
                });
            } finally {
                setIsCalculatingDuration(false);
            }
        }
    };

    useEffect(() => {
        fetchCourseData();
    }, [id]);

    const handleVideoSelect = (video) => {
        setSelectedVideo(video);
        setAllHomeworkReplies([]);
        fetchHomeworkReplies(video);
    };

    const toggleChapter = (index) => {
        const newExpanded = new Set(expandedChapters);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedChapters(newExpanded);
    };

    const togglePartie = (chapterIndex, partieIndex) => {
        const key = `${chapterIndex}-${partieIndex}`;
        const newExpanded = new Set(expandedParties);
        if (newExpanded.has(key)) {
            newExpanded.delete(key);
        } else {
            newExpanded.add(key);
        }
        setExpandedParties(newExpanded);
    };

    const handleDeleteVideo = async (chapterIndex, partieIndex, videoId) => {
        try {
            await deleteVideo(id, chapterIndex, partieIndex, videoId);
            fetchCourseData();
            setSelectedVideo(null);
            toast({
                title: "تم الحذف",
                description: "تم حذف الفيديو بنجاح",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-left"
            });
        } catch (error) {
            toast({
                title: "خطأ",
                description: "فشل حذف الفيديو",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-left"
            });
        }
    };

    const openVideoUploadModal = (chapterIndex, partieIndex) => {
        setSelectedChapterIndex(chapterIndex);
        setSelectedPartieIndex(partieIndex);
        setVideoUploadModalOpen(true);
    };

    const handleCloseVideoUploadModal = () => {
        setVideoUploadModalOpen(false);
        setNewVideo({
            name: '',
            description: '',
            duree: '',
            file: null,
        });
        setUploadProgress(0);
        setIsCalculatingDuration(false);
        setUploadAnimation(false);
    };

    const handleAddVideo = async () => {
        if (selectedChapterIndex !== null && selectedPartieIndex !== null && courseData && newVideo.file) {
            try {
                setUploadAnimation(true);
                const formData = new FormData();
                formData.append('video', newVideo.file);
                formData.append('nomvideo', newVideo.name);
                formData.append('description', newVideo.description);
                formData.append('duree', newVideo.duree);
                formData.append('chapitreIndex', selectedChapterIndex);
                formData.append('partieIndex', selectedPartieIndex);

                await axiosInstance.post(`/courses/course/${id}/videos`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percentCompleted);
                    },
                });

                setTimeout(() => {
                    fetchCourseData();
                    toast({
                        title: "تم الرفع بنجاح!",
                        description: `تم رفع الفيديو "${newVideo.name}" بنجاح`,
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                        position: "top-left"
                    });
                    handleCloseVideoUploadModal();
                }, 500);
            } catch (error) {
                setUploadAnimation(false);
                toast({
                    title: "خطأ في الرفع",
                    description: "فشل رفع الفيديو، يرجى المحاولة مرة أخرى",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                    position: "top-left"
                });
            }
        }
    };

    const handleDeletePartie = async (chapterIndex, partieIndex) => {
        try {
            const courseId = courseData._id;
            const chapitreId = courseData.chapitres[chapterIndex]._id;
            const partieId = courseData.chapitres[chapterIndex].parties[partieIndex]._id;

            await axiosInstance.delete(`courses/courses/${courseId}/chapters/${chapitreId}/parties/${partieId}`);

            const updatedCourseData = { ...courseData };
            updatedCourseData.chapitres[chapterIndex].parties.splice(partieIndex, 1);
            setCourseData(updatedCourseData);

            toast({
                title: "تم الحذف",
                description: "تم حذف الجزء بنجاح",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-left"
            });
        } catch (error) {
            toast({
                title: "خطأ",
                description: "فشل حذف الجزء",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-left"
            });
        }
    };

    const saveAttachmentsToPartie = async (chapterIndex, partieIndex, attachment) => {
        try {
            if (!attachment) {
                toast({
                    title: "خطأ",
                    description: "لم يتم توفير مرفق",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "top-left"
                });
                return;
            }

            const formData = new FormData();
            formData.append('file', attachment);
            formData.append('nom', AttchmentName);

            const course = courseData;
            if (!course) {
                toast({
                    title: "خطأ",
                    description: "لم يتم العثور على الدورة",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "top-left"
                });
                return;
            }

            const chapitreId = course.chapitres[chapterIndex]?._id;
            const partieId = course.chapitres[chapterIndex]?.parties[partieIndex]?._id;

            if (!chapitreId || !partieId) {
                toast({
                    title: "خطأ",
                    description: "لم يتم العثور على معرف ال محور أو الجزء",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "top-left"
                });
                return;
            }

            await axiosInstance.post(
                `/courses/attachment/${id}/chapters/${chapitreId}/parties/${partieId}/add`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            setAttachmentModalOpen(false);
            fetchCourseData();

            toast({
                title: "تم الرفع",
                description: "تم رفع المرفق بنجاح",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-left"
            });
        } catch (error) {
            toast({
                title: "خطأ",
                description: "فشل رفع المرفق",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-left"
            });
        }
    };

    const toggleChapterState = async (chapterId) => {
        try {
            await toggleChaptire(id, chapterId);
            fetchCourseData();
        } catch (error) {
            toast({
                title: "خطأ",
                description: "فشل تغيير حالة ال محور",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-left"
            });
        }
    };

    const handleDeleteAttachment = async (partieIndex, attIndex, chapIndex) => {
        try {
            const chapitreId = courseData.chapitres[chapIndex]._id;
            const partieId = courseData.chapitres[chapIndex].parties[partieIndex]._id;
            const attachmentId = courseData.chapitres[chapIndex].parties[partieIndex].attachments[attIndex]._id;

            await axiosInstance.delete(
                `/courses/attachment/${id}/chapters/${chapitreId}/parties/${partieId}/attachments/${attachmentId}`
            );

            const updatedParties = [...courseData.chapitres[chapIndex].parties];
            updatedParties[partieIndex].attachments.splice(attIndex, 1);

            setCourseData(prevData => {
                const updatedChapters = [...prevData.chapitres];
                updatedChapters[chapIndex].parties = updatedParties;
                return { ...prevData, chapitres: updatedChapters };
            });

            toast({
                title: "تم الحذف",
                description: "تم حذف المرفق بنجاح",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-left"
            });
        } catch (error) {
            toast({
                title: "خطأ",
                description: "فشل حذف المرفق",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-left"
            });
        }
    };

    const openNewPartieModal = (chapterId) => {
        setChaterID(chapterId);
        setisAddNewPartieOpen(true);
    };

    const handleReorderVideos = async (chapterIndex, partieIndex, videoIndex, direction) => {
        try {
            const updatedVideos = [...courseData.chapitres[chapterIndex].parties[partieIndex].videos];
            const temp = updatedVideos[videoIndex];

            if (direction === 'up' && videoIndex > 0) {
                updatedVideos[videoIndex] = updatedVideos[videoIndex - 1];
                updatedVideos[videoIndex - 1] = temp;
            } else if (direction === 'down' && videoIndex < updatedVideos.length - 1) {
                updatedVideos[videoIndex] = updatedVideos[videoIndex + 1];
                updatedVideos[videoIndex + 1] = temp;
            }

            await axiosInstance.put(
                `courses/updatedVideos/${id}/chapters/${chapterIndex}/parties/${partieIndex}/reorder-videos`,
                { videos: updatedVideos }
            );

            fetchCourseData();
        } catch (error) {
            toast({
                title: "خطأ",
                description: "فشل إعادة ترتيب الفيديوهات",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-left"
            });
        }
    };

    if (!courseData) {
        return (
            <Flex
                justify="center"
                align="center"
                h="100vh"
                bg={bgColor}
            >
                <VStack spacing={4}>
                    <Spinner
                        size="xl"
                        color={accentColor}
                        thickness="4px"
                    />
                    <Text color={textColor} fontSize="lg">جاري تحميل بيانات الدورة...</Text>
                </VStack>
            </Flex>
        );
    }

    return (
        <>
            <Container
                maxW="full"
                p={0}
                transition="margin 0.3s ease"
            >
                <Flex flex="1" direction="column" overflowY="auto">
                    {isMobile && (
                        <Box p={4} bg={cardBg} borderBottom="1px solid" borderColor={borderColor}>
                            <HStack justify="space-between">
                                <IconButton
                                    icon={<HamburgerIcon />}
                                    variant="ghost"
                                    onClick={() => { }} // No sidebar toggle in this version
                                    aria-label="Open menu"
                                />
                                <Text fontSize="lg" fontWeight="bold" color={accentColor}>
                                    تحرير الدورة
                                </Text>
                            </HStack>
                        </Box>
                    )}

                    <Box p={containerPadding}>
                        <Card mb={cardSpacing} bg={cardBg} borderRadius="2xl" border="1px solid" borderColor={borderColor}>
                            <CardHeader bg={sectionHeaderBg} color={textColor} py={6}>
                                <VStack align="start" spacing={4}>
                                    <HStack spacing={4}>
                                        <Box
                                            p={2}
                                            bg={bgColor}
                                            borderRadius="md"
                                        >
                                            <FaGraduationCap size={28} color={accentColor} />
                                        </Box>
                                        <VStack align="start" spacing={1}>
                                            <Heading
                                                size={headerSize}
                                                textAlign="right"
                                                fontWeight="bold"
                                                color={textColor}
                                            >
                                                الدورة: {courseData.name}
                                            </Heading>
                                            <Badge
                                                bg={accentBg}
                                                color={accentColor}
                                                fontSize={isMobile ? "sm" : "md"}
                                                px={3}
                                                py={1}
                                                borderRadius="full"
                                            >
                                                {courseData.chapitres?.length || 0} محور
                                            </Badge>
                                        </VStack>
                                    </HStack>
                                </VStack>
                            </CardHeader>
                        </Card>

                        <Stack
                            direction={{ base: "column", lg: "row" }}
                            spacing={cardSpacing}
                            align="stretch"
                        >
                            {/* Chapters List */}
                            <Box
                                flex="1"
                                maxW={{ base: "100%", lg: "45%" }}
                                order={{ base: 2, lg: 1 }}
                            >
                                <Card bg={cardBg} borderRadius="2xl" border="1px solid" borderColor={borderColor}>
                                    <CardHeader bg={sectionHeaderBg} color={textColor} py={5}>
                                        <Stack
                                            direction={{ base: "column", md: "row" }}
                                            justify="space-between"
                                            align={{ base: "stretch", md: "center" }}
                                            spacing={4}
                                        >
                                            <HStack spacing={3}>
                                                <Box
                                                    p={2}
                                                    bg={bgColor}
                                                    borderRadius="md"
                                                >
                                                    <FaBookOpen size={20} color={accentColor} />
                                                </Box>
                                                <Text
                                                    fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                                                    fontWeight="bold"
                                                    color={textColor}
                                                >
                                                    قائمة المحاور
                                                </Text>
                                            </HStack>
                                            <Button
                                                rightIcon={<AddIcon />}
                                                size={buttonSize}
                                                colorScheme="gray"
                                                color={accentColor}
                                                onClick={onOpen}
                                                borderRadius="md"
                                                px={6}
                                                w={{ base: "full", md: "auto" }}
                                                border="1px solid"
                                                borderColor={borderColor}
                                                bg="white"
                                                _hover={{
                                                    bg: sectionHeaderHoverBg,
                                                    color: accentColor
                                                }}
                                                transition="all 0.2s"
                                            >
                                                إضافة محور جديد
                                            </Button>
                                        </Stack>
                                    </CardHeader>

                                    <CardBody p={0}>
                                        <VStack spacing={0} align="stretch">
                                            {courseData.chapitres.map((chapter, chapterIndex) => (
                                                <Box key={chapter._id}>
                                                    {/* Chapter Header */}
                                                    <Box
                                                        bg={sectionHeaderBg}
                                                        color={textColor}
                                                        p={{ base: 5, md: 6 }}
                                                        cursor="pointer"
                                                        onClick={() => toggleChapter(chapterIndex)}
                                                        _hover={{
                                                            bg: sectionHeaderHoverBg
                                                        }}
                                                        borderBottom="1px solid"
                                                        borderColor={borderColor}
                                                        transition="all 0.2s"
                                                    >
                                                        <Stack
                                                            direction={{ base: "column", md: "row" }}
                                                            justify="space-between"
                                                            w="full"
                                                            spacing={4}
                                                            position="relative"
                                                            zIndex={1}
                                                        >
                                                            <HStack
                                                                spacing={4}
                                                                justify={{ base: "center", md: "flex-start" }}
                                                                flexWrap="wrap"
                                                            >
                                                                <Box
                                                                    transform={expandedChapters.has(chapterIndex) ? "rotate(90deg)" : "rotate(0deg)"}
                                                                    transition="transform 0.2s"
                                                                >
                                                                    <FaChevronRight size={18} color={accentColor} />
                                                                </Box>
                                                                <Tooltip
                                                                    label={!chapter.isOpen ? "محور مقفل" : "محور مفتوح"}
                                                                    placement="top"
                                                                >
                                                                    <IconButton
                                                                        icon={!chapter.isOpen ? <FiLock /> : <FiUnlock />}
                                                                        size="sm"
                                                                        bg="white"
                                                                        color={!chapter.isOpen ? lockRed : lockGreen}
                                                                        border="1px solid"
                                                                        borderColor={!chapter.isOpen ? lockRed : lockGreen}
                                                                        aria-label="Toggle lock"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleChapterState(chapter._id);
                                                                        }}
                                                                        borderRadius="full"
                                                                        _hover={{
                                                                            bg: chapter.isOpen ? lockRed : lockGreen,
                                                                            color: "white"
                                                                        }}
                                                                        transition="all 0.2s"
                                                                    />
                                                                </Tooltip>
                                                                <Badge
                                                                    bg="white"
                                                                    color={accentColor}
                                                                    borderRadius="full"
                                                                    px={3}
                                                                    py={1}
                                                                    border="1px solid"
                                                                    borderColor={borderColor}
                                                                >
                                                                    {chapter.parties?.length || 0} جزء
                                                                </Badge>
                                                            </HStack>
                                                            <VStack
                                                                align={{ base: "center", md: "end" }}
                                                                spacing={1}
                                                            >
                                                                <Text
                                                                    fontWeight="bold"
                                                                    fontSize={{ base: "lg", md: "xl" }}
                                                                    textAlign={{ base: "center", md: "right" }}
                                                                    color={textColor}
                                                                >
                                                                    {chapter.nom}
                                                                </Text>
                                                                <Text
                                                                    fontSize="sm"
                                                                    opacity={0.8}
                                                                    textAlign={{ base: "center", md: "right" }}
                                                                    noOfLines={2}
                                                                    color={textColor}
                                                                >
                                                                    {chapter.description}
                                                                </Text>
                                                            </VStack>
                                                        </Stack>
                                                    </Box>

                                                    {/* Chapter Content */}
                                                    <Collapse in={expandedChapters.has(chapterIndex)} animateOpacity>
                                                        <Box bg={partieHeaderBg} p={0}>
                                                            <VStack spacing={0} align="stretch">
                                                                {chapter.parties?.map((partie, partieIndex) => (
                                                                    <SlideFade
                                                                        key={partie._id}
                                                                        in={expandedChapters.has(chapterIndex)}
                                                                        offsetY="20px"
                                                                        delay={partieIndex * 0.05}
                                                                    >
                                                                        <Box m={3}>
                                                                            {/* Partie Header */}
                                                                            <Box
                                                                                bg={partieHeaderBg}
                                                                                color={textColor}
                                                                                p={{ base: 3, md: 4 }}
                                                                                borderRadius="md"
                                                                                cursor="pointer"
                                                                                onClick={() => togglePartie(chapterIndex, partieIndex)}
                                                                                _hover={{
                                                                                    bg: partieHeaderHoverBg
                                                                                }}
                                                                                border="1px solid"
                                                                                borderColor={borderColor}
                                                                                transition="all 0.2s"
                                                                            >
                                                                                <Stack
                                                                                    direction={{ base: "column", md: "row" }}
                                                                                    justify="space-between"
                                                                                    spacing={3}
                                                                                    position="relative"
                                                                                    zIndex={1}
                                                                                >
                                                                                    <HStack
                                                                                        spacing={2}
                                                                                        justify={{ base: "center", md: "flex-start" }}
                                                                                        flexWrap="wrap"
                                                                                    >
                                                                                        <Box
                                                                                            transform={expandedParties.has(`${chapterIndex}-${partieIndex}`) ? "rotate(90deg)" : "rotate(0deg)"}
                                                                                            transition="transform 0.2s"
                                                                                        >
                                                                                            <FaChevronRight size={14} color={accentColor} />
                                                                                        </Box>
                                                                                        <Tooltip label="حذف الجزء">
                                                                                            <IconButton
                                                                                                icon={<DeleteIcon />}
                                                                                                size="sm"
                                                                                                bg="white"
                                                                                                color={lockRed}
                                                                                                border="1px solid"
                                                                                                borderColor={lockRed}
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleDeletePartie(chapterIndex, partieIndex);
                                                                                                }}
                                                                                                borderRadius="full"
                                                                                                _hover={{
                                                                                                    bg: lockRed,
                                                                                                    color: "white"
                                                                                                }}
                                                                                                transition="all 0.2s"
                                                                                            />
                                                                                        </Tooltip>
                                                                                        <Tooltip label="إضافة مرفق">
                                                                                            <IconButton
                                                                                                icon={<FaPaperclip />}
                                                                                                size="sm"
                                                                                                bg="white"
                                                                                                color={accentColor}
                                                                                                border="1px solid"
                                                                                                borderColor={accentColor}
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    setSelectedChapterIndex(chapterIndex);
                                                                                                    setSelectedPartieIndex(partieIndex);
                                                                                                    setAttachmentModalOpen(true);
                                                                                                }}
                                                                                                borderRadius="full"
                                                                                                _hover={{
                                                                                                    bg: accentColor,
                                                                                                    color: "white"
                                                                                                }}
                                                                                                transition="all 0.2s"
                                                                                            />
                                                                                        </Tooltip>
                                                                                        <Tooltip label="إضافة فيديو">
                                                                                            <IconButton
                                                                                                icon={<FaVideo />}
                                                                                                size="sm"
                                                                                                bg="white"
                                                                                                color={lockGreen}
                                                                                                border="1px solid"
                                                                                                borderColor={lockGreen}
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    openVideoUploadModal(chapterIndex, partieIndex);
                                                                                                }}
                                                                                                borderRadius="full"
                                                                                                _hover={{
                                                                                                    bg: lockGreen,
                                                                                                    color: "white"
                                                                                                }}
                                                                                                transition="all 0.2s"
                                                                                            />
                                                                                        </Tooltip>
                                                                                        <Badge
                                                                                            bg="white"
                                                                                            color={accentColor}
                                                                                            borderRadius="full"
                                                                                            px={2}
                                                                                            py={0.5}
                                                                                            border="1px solid"
                                                                                            borderColor={borderColor}
                                                                                        >
                                                                                            {partie.videos?.length || 0} فيديو
                                                                                        </Badge>
                                                                                    </HStack>
                                                                                    <VStack
                                                                                        align={{ base: "center", md: "end" }}
                                                                                        spacing={0}
                                                                                    >
                                                                                        <Text
                                                                                            fontWeight="semibold"
                                                                                            fontSize={{ base: "md", md: "lg" }}
                                                                                            textAlign={{ base: "center", md: "right" }}
                                                                                            color={textColor}
                                                                                        >
                                                                                            {partie.name}
                                                                                        </Text>
                                                                                        <Text
                                                                                            fontSize="sm"
                                                                                            opacity={0.8}
                                                                                            textAlign={{ base: "center", md: "right" }}
                                                                                            noOfLines={2}
                                                                                            color={textColor}
                                                                                        >
                                                                                            {partie.description}
                                                                                        </Text>
                                                                                    </VStack>
                                                                                </Stack>
                                                                            </Box>

                                                                            {/* Partie Content */}
                                                                            <Collapse in={expandedParties.has(`${chapterIndex}-${partieIndex}`)} animateOpacity>
                                                                                <Box
                                                                                    bg="white"
                                                                                    borderRadius="md"
                                                                                    mt={2}
                                                                                    p={4}
                                                                                    border="1px solid"
                                                                                    borderColor={borderColor}
                                                                                >
                                                                                    {/* Videos List */}
                                                                                    <VStack spacing={3} align="stretch">
                                                                                        {partie.videos?.map((video, videoIndex) => (
                                                                                            <ScaleFade
                                                                                                key={video._id}
                                                                                                in={expandedParties.has(`${chapterIndex}-${partieIndex}`)}
                                                                                                delay={videoIndex * 0.05}
                                                                                            >
                                                                                                <Card
                                                                                                    variant="outline"
                                                                                                    size="sm"
                                                                                                    cursor="pointer"
                                                                                                    onClick={() => handleVideoSelect(video)}
                                                                                                    bg={selectedVideo?._id === video._id ? accentBg : "white"}
                                                                                                    borderColor={selectedVideo?._id === video._id ? accentColor : borderColor}
                                                                                                    borderWidth="2px"
                                                                                                    borderRadius="md"
                                                                                                    _hover={{
                                                                                                        bg: accentBg,
                                                                                                        borderColor: accentColor
                                                                                                    }}
                                                                                                    transition="all 0.2s"
                                                                                                >
                                                                                                    <CardBody p={3}>
                                                                                                        <Stack
                                                                                                            direction={{ base: "column", sm: "row" }}
                                                                                                            justify="space-between"
                                                                                                            align={{ base: "stretch", sm: "center" }}
                                                                                                            spacing={2}
                                                                                                        >
                                                                                                            <HStack
                                                                                                                spacing={1}
                                                                                                                justify={{ base: "center", sm: "flex-start" }}
                                                                                                                flexWrap="wrap"
                                                                                                            >
                                                                                                                <Tooltip label="حذف الفيديو">
                                                                                                                    <IconButton
                                                                                                                        icon={<FaTrash />}
                                                                                                                        size="xs"
                                                                                                                        bg="white"
                                                                                                                        color={lockRed}
                                                                                                                        border="1px solid"
                                                                                                                        borderColor={lockRed}
                                                                                                                        variant="ghost"
                                                                                                                        onClick={(e) => {
                                                                                                                            e.stopPropagation();
                                                                                                                            handleDeleteVideo(chapterIndex, partieIndex, video._id);
                                                                                                                        }}
                                                                                                                        borderRadius="full"
                                                                                                                        _hover={{
                                                                                                                            bg: lockRed,
                                                                                                                            color: "white"
                                                                                                                        }}
                                                                                                                    />
                                                                                                                </Tooltip>
                                                                                                                <Tooltip label="تحريك لأعلى">
                                                                                                                    <IconButton
                                                                                                                        icon={<FaArrowUp />}
                                                                                                                        size="xs"
                                                                                                                        variant="ghost"
                                                                                                                        bg="white"
                                                                                                                        color={accentColor}
                                                                                                                        border="1px solid"
                                                                                                                        borderColor={accentColor}
                                                                                                                        onClick={(e) => {
                                                                                                                            e.stopPropagation();
                                                                                                                            handleReorderVideos(chapterIndex, partieIndex, videoIndex, 'up');
                                                                                                                        }}
                                                                                                                        isDisabled={videoIndex === 0}
                                                                                                                        borderRadius="full"
                                                                                                                        _hover={{
                                                                                                                            bg: accentColor,
                                                                                                                            color: "white"
                                                                                                                        }}
                                                                                                                    />
                                                                                                                </Tooltip>
                                                                                                                <Tooltip label="تحريك لأسفل">
                                                                                                                    <IconButton
                                                                                                                        icon={<FaArrowDown />}
                                                                                                                        size="xs"
                                                                                                                        variant="ghost"
                                                                                                                        bg="white"
                                                                                                                        color={accentColor}
                                                                                                                        border="1px solid"
                                                                                                                        borderColor={accentColor}
                                                                                                                        onClick={(e) => {
                                                                                                                            e.stopPropagation();
                                                                                                                            handleReorderVideos(chapterIndex, partieIndex, videoIndex, 'down');
                                                                                                                        }}
                                                                                                                        isDisabled={videoIndex === partie.videos.length - 1}
                                                                                                                        borderRadius="full"
                                                                                                                        _hover={{
                                                                                                                            bg: accentColor,
                                                                                                                            color: "white"
                                                                                                                        }}
                                                                                                                    />
                                                                                                                </Tooltip>
                                                                                                                <Badge
                                                                                                                    colorScheme="gray"
                                                                                                                    size="sm"
                                                                                                                    borderRadius="full"
                                                                                                                    px={2}
                                                                                                                    py={0.5}
                                                                                                                    color={accentColor}
                                                                                                                    bg="white"
                                                                                                                    border="1px solid"
                                                                                                                    borderColor={borderColor}
                                                                                                                >
                                                                                                                    {video.duree}
                                                                                                                </Badge>
                                                                                                            </HStack>
                                                                                                            <VStack
                                                                                                                align={{ base: "center", sm: "end" }}
                                                                                                                spacing={0}
                                                                                                            >
                                                                                                                <Text
                                                                                                                    fontWeight="medium"
                                                                                                                    fontSize="sm"
                                                                                                                    color={accentColor}
                                                                                                                    textAlign={{ base: "center", sm: "right" }}
                                                                                                                >
                                                                                                                    {video.nomvideo}
                                                                                                                </Text>
                                                                                                                <Text
                                                                                                                    fontSize="xs"
                                                                                                                    color={textColor}
                                                                                                                    opacity={0.7}
                                                                                                                    textAlign={{ base: "center", sm: "right" }}
                                                                                                                    noOfLines={2}
                                                                                                                >
                                                                                                                    {video.description}
                                                                                                                </Text>
                                                                                                            </VStack>
                                                                                                        </Stack>
                                                                                                    </CardBody>
                                                                                                </Card>
                                                                                            </ScaleFade>
                                                                                        ))}
                                                                                    </VStack>

                                                                                    {/* Attachments Section */}
                                                                                    {partie.attachments?.length > 0 && (
                                                                                        <Box mt={4}>
                                                                                            <Divider mb={3} />
                                                                                            <Text
                                                                                                fontSize="sm"
                                                                                                fontWeight="semibold"
                                                                                                color={accentColor}
                                                                                                mb={2}
                                                                                            >
                                                                                                المرفقات:
                                                                                            </Text>
                                                                                            <VStack spacing={2} align="stretch">
                                                                                                {partie.attachments.map((attachment, attIndex) => (
                                                                                                    <HStack
                                                                                                        onClick={() => window.open(urlvideo + attachment.url)}
                                                                                                        key={attIndex}
                                                                                                        justify="space-between"
                                                                                                        p={2}
                                                                                                        bg="gray.50"
                                                                                                        borderRadius="md"
                                                                                                        cursor="pointer"
                                                                                                        border="1px solid"
                                                                                                        borderColor={borderColor}
                                                                                                        _hover={{
                                                                                                            bg: "gray.100"
                                                                                                        }}
                                                                                                        transition="all 0.2s"
                                                                                                    >
                                                                                                        <IconButton
                                                                                                            icon={<FaTrash />}
                                                                                                            size="xs"
                                                                                                            bg="white"
                                                                                                            color={lockRed}
                                                                                                            border="1px solid"
                                                                                                            borderColor={lockRed}
                                                                                                            variant="ghost"
                                                                                                            onClick={() => handleDeleteAttachment(partieIndex, attIndex, chapterIndex)}
                                                                                                            borderRadius="full"
                                                                                                            _hover={{
                                                                                                                bg: lockRed,
                                                                                                                color: "white"
                                                                                                            }}
                                                                                                        />
                                                                                                        <Text fontSize="sm" textAlign="right" fontWeight="medium" color={textColor}>
                                                                                                            {attachment.nom}
                                                                                                        </Text>
                                                                                                    </HStack>
                                                                                                ))}
                                                                                            </VStack>
                                                                                        </Box>
                                                                                    )}
                                                                                </Box>
                                                                            </Collapse>
                                                                        </Box>
                                                                    </SlideFade>
                                                                ))}

                                                                {/* Add New Partie Button */}
                                                                <Box p={4}>
                                                                    <Button
                                                                        leftIcon={<AddIcon />}
                                                                        onClick={() => openNewPartieModal(chapter._id)}
                                                                        variant="outline"
                                                                        colorScheme="gray"
                                                                        size={buttonSize}
                                                                        w="full"
                                                                        borderRadius="md"
                                                                        borderWidth="1px"
                                                                        borderColor={borderColor}
                                                                        _hover={{
                                                                            bg: sectionHeaderHoverBg,
                                                                            color: accentColor
                                                                        }}
                                                                        transition="all 0.2s"
                                                                    >
                                                                        إضافة جزء جديد
                                                                    </Button>
                                                                </Box>
                                                            </VStack>
                                                        </Box>
                                                    </Collapse>
                                                </Box>
                                            ))}
                                        </VStack>
                                    </CardBody>
                                </Card>
                            </Box>

                            {/* Video Player Section */}
                            <Box
                                flex="1"
                                maxW={{ base: "100%", lg: "55%" }}
                                order={{ base: 1, lg: 2 }}
                            >
                                <Card bg={cardBg} borderRadius="2xl" border="1px solid" borderColor={borderColor}>
                                    <CardHeader bg={sectionHeaderBg} color={textColor} py={5}>
                                        <Stack
                                            direction={{ base: "column", md: "row" }}
                                            justify="space-between"
                                            align={{ base: "center", md: "center" }}
                                            spacing={4}
                                        >
                                            <HStack spacing={3}>
                                                <Box
                                                    p={2}
                                                    bg={bgColor}
                                                    borderRadius="md"
                                                >
                                                    <FaPlay size={20} color={accentColor} />
                                                </Box>
                                                <Badge
                                                    bg={accentBg}
                                                    color={accentColor}
                                                    fontSize={{ base: "sm", md: "md" }}
                                                    px={3}
                                                    py={1}
                                                    borderRadius="full"
                                                    border="1px solid"
                                                    borderColor={borderColor}
                                                >
                                                    مشغل الفيديو
                                                </Badge>
                                            </HStack>
                                            <Text
                                                fontSize={{ base: "lg", md: "xl" }}
                                                fontWeight="bold"
                                                color={textColor}
                                            >
                                                عرض المحتوى
                                            </Text>
                                        </Stack>
                                    </CardHeader>

                                    <CardBody p={{ base: 4, md: 6 }}>
                                        {selectedVideo ? (
                                            <VStack spacing={5} align="stretch">
                                                <Card variant="outline" borderRadius="md" borderWidth="1.5px" borderColor={borderColor}>
                                                    <CardBody p={0}>
                                                        <VideoPlayer
                                                            idvdocipher={selectedVideo.idvdocipher}
                                                            videoId={selectedVideo.idvdocipher}
                                                            video={selectedVideo}
                                                        />
                                                    </CardBody>
                                                </Card>

                                                <VStack align="start" spacing={3}>
                                                    <Heading
                                                        size={{ base: "md", md: "lg" }}
                                                        color={accentColor}
                                                        textAlign="right"
                                                    >
                                                        {selectedVideo.nomvideo}
                                                    </Heading>

                                                    <Stack
                                                        direction={{ base: "column", sm: "row" }}
                                                        spacing={3}
                                                        w="full"
                                                    >
                                                        <Badge
                                                            colorScheme="gray"
                                                            fontSize="sm"
                                                            px={3}
                                                            py={1}
                                                            borderRadius="full"
                                                            color={accentColor}
                                                            bg="white"
                                                            border="1px solid"
                                                            borderColor={borderColor}
                                                        >
                                                            <FaClock style={{ marginLeft: 4 }} />
                                                            المدة: {selectedVideo.duree}
                                                        </Badge>
                                                        <Badge
                                                            colorScheme="gray"
                                                            fontSize="sm"
                                                            px={3}
                                                            py={1}
                                                            borderRadius="full"
                                                            color={accentColor}
                                                            bg="white"
                                                            border="1px solid"
                                                            borderColor={borderColor}
                                                        >
                                                            <FaEye style={{ marginLeft: 4 }} />
                                                            جاري العرض
                                                        </Badge>
                                                    </Stack>

                                                    <Box
                                                        p={4}
                                                        bg="gray.50"
                                                        borderRadius="md"
                                                        w="full"
                                                        border="1px solid"
                                                        borderColor={borderColor}
                                                    >
                                                        <Text
                                                            color={textColor}
                                                            lineHeight="tall"
                                                            textAlign="right"
                                                            fontSize={{ base: "sm", md: "md" }}
                                                        >
                                                            {selectedVideo.description}
                                                        </Text>
                                                    </Box>

                                                    {/* ===== Homework Replies Panel ===== */}
                                                    <Box
                                                        mt={2}
                                                        p={5}
                                                        bg={partieHeaderBg}
                                                        borderRadius="xl"
                                                        border="1px solid"
                                                        borderColor={borderColor}
                                                        w="full"
                                                    >
                                                        <HStack justify="space-between" mb={4} flexWrap="wrap" spacing={3}>
                                                            <HStack spacing={2}>
                                                                <Text fontSize="lg">📋</Text>
                                                                <Heading size="sm" fontWeight="700" color={accentColor}>
                                                                    ردود الواجب المنزلي
                                                                </Heading>
                                                                <Badge colorScheme="blue" borderRadius="full" px={2} fontSize="xs">
                                                                    {allHomeworkReplies.length} طالب
                                                                </Badge>
                                                            </HStack>
                                                            <HStack spacing={2}>
                                                                <Button
                                                                    size="xs"
                                                                    variant="outline"
                                                                    colorScheme="blue"
                                                                    borderRadius="full"
                                                                    onClick={() => fetchHomeworkReplies(selectedVideo)}
                                                                    isLoading={isLoadingHomework}
                                                                >
                                                                    تحديث
                                                                </Button>
                                                                {selectedVideo?.createdAt &&
                                                                    Date.now() - new Date(selectedVideo.createdAt).getTime() >= 72 * 60 * 60 * 1000 && (
                                                                        <Button
                                                                            size="xs"
                                                                            bgGradient="linear(to-r, orange.400, red.400)"
                                                                            color="white"
                                                                            borderRadius="full"
                                                                            fontWeight="700"
                                                                            onClick={() => setIsSmsModalOpen(true)}
                                                                            _hover={{ bgGradient: 'linear(to-r, orange.500, red.500)' }}
                                                                        >
                                                                            📱 SMS للغائبين
                                                                        </Button>
                                                                    )}
                                                            </HStack>
                                                        </HStack>

                                                        {isLoadingHomework ? (
                                                            <Center py={6}><Spinner color={accentColor} /></Center>
                                                        ) : allHomeworkReplies.length === 0 ? (
                                                            <Center py={5}>
                                                                <VStack spacing={1}>
                                                                    <Text fontSize="xl">🖼️</Text>
                                                                    <Text color="gray.400" fontSize="sm">لا توجد ردود بعد</Text>
                                                                </VStack>
                                                            </Center>
                                                        ) : (
                                                            <VStack align="stretch" spacing={4}>
                                                                {allHomeworkReplies.map((reply) => (
                                                                    <Box
                                                                        key={reply._id}
                                                                        p={4}
                                                                        bg={cardBg}
                                                                        borderRadius="lg"
                                                                        border="1px solid"
                                                                        borderColor={borderColor}
                                                                        boxShadow="sm"
                                                                    >
                                                                        <HStack mb={3} spacing={3}>
                                                                            <Avatar size="sm" name={reply.studentId?.username} />
                                                                            <VStack align="flex-start" spacing={0}>
                                                                                <Text fontWeight="700" fontSize="sm" color={textColor}>
                                                                                    {reply.studentId?.username}
                                                                                </Text>
                                                                                <Text fontSize="xs" color="gray.500">
                                                                                    {new Date(reply.submittedAt).toLocaleString('ar-EG')}
                                                                                </Text>
                                                                            </VStack>
                                                                            <Badge colorScheme="green" borderRadius="full" px={2} mr="auto" fontSize="xs">
                                                                                ✅ أرسل
                                                                            </Badge>
                                                                        </HStack>
                                                                        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                                                                            {reply.photoUrls.map((url, i) => (
                                                                                <Image
                                                                                    key={i}
                                                                                    src={`https://api.cobra-education.com${url}`}
                                                                                    borderRadius="lg"
                                                                                    objectFit="cover"
                                                                                    h="90px"
                                                                                    w="100%"
                                                                                    cursor="pointer"
                                                                                    onClick={() => window.open(`https://api.cobra-education.com${url}`, '_blank')}
                                                                                    boxShadow="sm"
                                                                                    _hover={{ transform: 'scale(1.03)', transition: 'all 0.2s' }}
                                                                                    alt={`واجب ${reply.studentId?.username}`}
                                                                                />
                                                                            ))}
                                                                        </SimpleGrid>
                                                                    </Box>
                                                                ))}
                                                            </VStack>
                                                        )}
                                                    </Box>
                                                </VStack>
                                            </VStack>
                                        ) : (
                                            <VStack
                                                spacing={5}
                                                align="center"
                                                justify="center"
                                                h={{ base: "300px", md: "400px" }}
                                                color={textColor}
                                            >
                                                <Box
                                                    p={6}
                                                    bg="gray.100"
                                                    borderRadius="full"
                                                    opacity={0.5}
                                                >
                                                    <FaVideo size={isMobile ? 40 : 56} />
                                                </Box>
                                                <VStack spacing={2}>
                                                    <Text
                                                        fontSize={{ base: "lg", md: "xl" }}
                                                        fontWeight="semibold"
                                                    >
                                                        لم يتم اختيار فيديو
                                                    </Text>
                                                    <Text
                                                        opacity={0.7}
                                                        textAlign="center"
                                                        fontSize={{ base: "sm", md: "md" }}
                                                    >
                                                        اختر فيديو من قائمة المحاور لبدء المشاهدة
                                                    </Text>
                                                </VStack>
                                            </VStack>
                                        )}
                                    </CardBody>
                                </Card>
                            </Box>
                        </Stack>
                    </Box>
                </Flex>
            </Container>

            {/* ===== SMS Reminder Modal ===== */}
            <Modal isOpen={isSmsModalOpen} onClose={() => setIsSmsModalOpen(false)} isCentered size="md" dir="rtl">
                <ModalOverlay backdropFilter="blur(8px)" />
                <ModalContent borderRadius="2xl" fontFamily="Cairo, sans-serif" dir="rtl" mx={isMobile ? 4 : 0}>
                    <ModalHeader
                        bg="linear-gradient(to-r, #ED8936, #E53E3E)"
                        bgGradient="linear(to-r, orange.400, red.400)"
                        color="white"
                        borderTopRadius="2xl"
                        textAlign="right"
                        fontWeight="700"
                        py={4}
                        px={6}
                    >
                        📱 إرسال رسالة SMS إلى الغائبين
                    </ModalHeader>
                    <ModalCloseButton color="white" top={3} left={3} right="auto" />
                    <ModalBody py={5} px={6}>
                        <VStack spacing={4} align="stretch">
                            <Text color="gray.600" fontSize="sm" textAlign="right">
                                سيتم إرسال رسالة عربية للطلاب الذين لم يرسلوا واجباتهم عن الفيديو:
                            </Text>
                            <Box p={3} bg="orange.50" borderRadius="lg" border="1px solid" borderColor="orange.200">
                                <Text fontWeight="700" color="orange.700" textAlign="right" fontSize="sm">
                                    🎬 {selectedVideo?.nomvideo}
                                </Text>
                            </Box>
                            <Text color="gray.500" fontSize="xs" textAlign="right">
                                اختر الجهة:
                            </Text>
                        </VStack>
                    </ModalBody>
                    <ModalFooter gap={3} justifyContent="center" pb={5}>
                        <Button
                            bgGradient="linear(to-r, blue.400, purple.400)"
                            color="white"
                            borderRadius="full"
                            fontWeight="700"
                            size="md"
                            px={6}
                            isLoading={isSendingSms}
                            loadingText="جاري..."
                            onClick={() => handleSendSms('students')}
                            _hover={{ bgGradient: 'linear(to-r, blue.500, purple.500)', transform: 'translateY(-2px)' }}
                            transition="all 0.2s"
                        >
                            👨‍🎓 إرسال للطلاب
                        </Button>
                        <Button
                            bgGradient="linear(to-r, green.400, teal.400)"
                            color="white"
                            borderRadius="full"
                            fontWeight="700"
                            size="md"
                            px={6}
                            isLoading={isSendingSms}
                            loadingText="جاري..."
                            onClick={() => handleSendSms('parents')}
                            _hover={{ bgGradient: 'linear(to-r, green.500, teal.500)', transform: 'translateY(-2px)' }}
                            transition="all 0.2s"
                        >
                            👨‍👩‍👦 إرسال لأولياء الأمور
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Video Upload Modal */}
            <Modal
                isOpen={videoUploadModalOpen}
                onClose={handleCloseVideoUploadModal}
                size={modalSize}
                isCentered
                closeOnOverlayClick={!uploadAnimation}
            >
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent
                    borderRadius="2xl"
                    overflow="hidden"
                    mx={isMobile ? 4 : 0}
                    border="1px solid"
                    borderColor={borderColor}
                >
                    <ModalHeader
                        bg={sectionHeaderBg}
                        color={textColor}
                        textAlign="center"
                        py={6}
                    >
                        <VStack spacing={2}>
                            <Box
                                p={3}
                                bg={bgColor}
                                borderRadius="md"
                            >
                                <FaVideo size={28} color={accentColor} />
                            </Box>
                            <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">
                                {uploadAnimation ? "جاري رفع الفيديو..." : "رفع فيديو جديد"}
                            </Text>
                            <Text fontSize="sm" opacity={0.8}>
                                {uploadAnimation ? "يرجى الانتظار حتى اكتمال الرفع" : "اختر ملف فيديو وسيتم حساب المدة تلقائياً"}
                            </Text>
                        </VStack>
                    </ModalHeader>
                    <ModalCloseButton color={textColor} isDisabled={uploadAnimation} />

                    <ModalBody p={{ base: 6, md: 8 }}>
                        <VStack spacing={6}>
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="md">
                                    اسم الفيديو
                                </FormLabel>
                                <Input
                                    value={newVideo.name}
                                    onChange={(e) => setNewVideo({ ...newVideo, name: e.target.value })}
                                    placeholder="أدخل اسم الفيديو"
                                    borderRadius="md"
                                    borderWidth="1.5px"
                                    borderColor={borderColor}
                                    bg="gray.50"
                                    py={4}
                                    fontSize="md"
                                    isDisabled={uploadAnimation}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="md">
                                    وصف الفيديو
                                </FormLabel>
                                <Textarea
                                    value={newVideo.description}
                                    onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                                    placeholder="أدخل وصف للفيديو"
                                    borderRadius="md"
                                    borderWidth="1.5px"
                                    borderColor={borderColor}
                                    bg="gray.50"
                                    py={3}
                                    fontSize="md"
                                    rows={3}
                                    resize="vertical"
                                    isDisabled={uploadAnimation}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="md">
                                    مدة الفيديو
                                    {isCalculatingDuration && (
                                        <Spinner size="sm" color={accentColor} ml={2} />
                                    )}
                                </FormLabel>
                                <HStack spacing={3}>
                                    <Input
                                        value={newVideo.duree}
                                        onChange={(e) => setNewVideo({ ...newVideo, duree: e.target.value })}
                                        placeholder="مثال: 10:30"
                                        borderRadius="md"
                                        borderWidth="1.5px"
                                        borderColor={borderColor}
                                        bg="gray.50"
                                        py={4}
                                        fontSize="md"
                                        isDisabled={uploadAnimation || isCalculatingDuration}
                                    />
                                    {newVideo.duree && (
                                        <Badge
                                            colorScheme="gray"
                                            fontSize="md"
                                            px={3}
                                            py={1}
                                            borderRadius="md"
                                            color={accentColor}
                                            bg="white"
                                            border="1px solid"
                                            borderColor={borderColor}
                                        >
                                            <FaClock style={{ marginLeft: 4 }} />
                                            {newVideo.duree}
                                        </Badge>
                                    )}
                                </HStack>
                                {isCalculatingDuration && (
                                    <Text fontSize="sm" color={accentColor} mt={2} fontWeight="medium">
                                        جاري حساب مدة الفيديو تلقائياً...
                                    </Text>
                                )}
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="md">
                                    ملف الفيديو
                                </FormLabel>
                                <Box
                                    position="relative"
                                    borderRadius="md"
                                    borderWidth="2px"
                                    borderStyle="dashed"
                                    borderColor={newVideo.file ? lockGreen : borderColor}
                                    bg={newVideo.file ? "green.50" : "gray.50"}
                                    p={6}
                                    textAlign="center"
                                    cursor={uploadAnimation ? "not-allowed" : "pointer"}
                                    _hover={{
                                        borderColor: uploadAnimation ? borderColor : accentColor,
                                        bg: uploadAnimation ? "gray.50" : accentBg
                                    }}
                                    transition="all 0.2s"
                                    onClick={() => !uploadAnimation && document.getElementById('video-file-input').click()}
                                >
                                    <Input
                                        id="video-file-input"
                                        type="file"
                                        accept="video/*"
                                        onChange={(e) => handleVideoFileChange(e.target.files[0])}
                                        position="absolute"
                                        opacity={0}
                                        w="full"
                                        h="full"
                                        cursor="pointer"
                                        isDisabled={uploadAnimation}
                                    />
                                    <VStack spacing={2}>
                                        <Box
                                            p={3}
                                            bg={newVideo.file ? "green.100" : "gray.200"}
                                            borderRadius="md"
                                            color={newVideo.file ? lockGreen : "gray.600"}
                                        >
                                            <FaVideo size={24} />
                                        </Box>
                                        <Text
                                            fontSize="md"
                                            fontWeight="bold"
                                            color={newVideo.file ? lockGreen : textColor}
                                        >
                                            {newVideo.file ? "تم اختيار الملف ✓" : "اختر ملف الفيديو"}
                                        </Text>
                                        <Text
                                            fontSize="sm"
                                            color="gray.600"
                                            textAlign="center"
                                        >
                                            {newVideo.file
                                                ? `${newVideo.file.name} (${(newVideo.file.size / (1024 * 1024)).toFixed(2)} MB)`
                                                : "اسحب الملف هنا أو انقر للاختيار"
                                            }
                                        </Text>
                                    </VStack>
                                </Box>
                            </FormControl>

                            {(uploadProgress > 0 || uploadAnimation) && (
                                <Box w="full">
                                    <VStack spacing={3}>
                                        <HStack justify="space-between" w="full">
                                            <Text fontWeight="bold" color={textColor} fontSize="md">
                                                {uploadProgress === 100 ? "تم الرفع بنجاح!" : `جاري الرفع: ${uploadProgress}%`}
                                            </Text>
                                            <Badge
                                                colorScheme={uploadProgress === 100 ? "green" : "blue"}
                                                fontSize="md"
                                                px={3}
                                                py={1}
                                                borderRadius="md"
                                            >
                                                {uploadProgress}%
                                            </Badge>
                                        </HStack>
                                        <Box w="full" position="relative">
                                            <Progress
                                                value={uploadProgress}
                                                colorScheme={uploadProgress === 100 ? "green" : "blue"}
                                                borderRadius="md"
                                                size="lg"
                                                bg="gray.200"
                                                hasStripe={uploadProgress < 100}
                                                isAnimated={uploadProgress < 100}
                                            />
                                            {uploadProgress === 100 && (
                                                <Box
                                                    position="absolute"
                                                    top="50%"
                                                    left="50%"
                                                    transform="translate(-50%, -50%)"
                                                    color="white"
                                                    fontWeight="bold"
                                                    fontSize="sm"
                                                >
                                                    مكتمل ✓
                                                </Box>
                                            )}
                                        </Box>
                                        {uploadAnimation && uploadProgress < 100 && (
                                            <Text fontSize="sm" color={accentColor} textAlign="center">
                                                يرجى عدم إغلاق النافذة أثناء الرفع...
                                            </Text>
                                        )}
                                    </VStack>
                                </Box>
                            )}
                        </VStack>
                    </ModalBody>

                    <ModalFooter p={{ base: 6, md: 8 }} pt={0}>
                        <Stack
                            direction={{ base: "column", sm: "row" }}
                            spacing={3}
                            w="full"
                        >
                            <Button
                                variant="outline"
                                onClick={handleCloseVideoUploadModal}
                                borderRadius="md"
                                borderWidth="1.5px"
                                px={6}
                                py={4}
                                fontSize="md"
                                w={{ base: "full", sm: "auto" }}
                                isDisabled={uploadAnimation}
                                _hover={{
                                    bg: sectionHeaderHoverBg
                                }}
                                transition="all 0.2s"
                            >
                                إلغاء
                            </Button>
                            <Button
                                colorScheme="blue"
                                color="white"
                                onClick={handleAddVideo}
                                isDisabled={!newVideo.file || !newVideo.name || uploadAnimation || isCalculatingDuration}
                                isLoading={uploadAnimation}
                                loadingText="جاري الرفع..."
                                borderRadius="md"
                                px={6}
                                py={4}
                                fontSize="md"
                                fontWeight="bold"
                                w={{ base: "full", sm: "auto" }}
                                _hover={{
                                    bg: accentColor
                                }}
                                transition="all 0.2s"
                                rightIcon={!uploadAnimation ? <FaVideo /> : undefined}
                            >
                                {uploadAnimation ? "جاري الرفع..." : "رفع الفيديو"}
                            </Button>
                        </Stack>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Attachment Upload Modal */}
            <Modal
                isOpen={attachmentModalOpen}
                onClose={() => setAttachmentModalOpen(false)}
                size={modalSize}
                isCentered
            >
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent borderRadius="2xl" overflow="hidden" mx={isMobile ? 4 : 0} border="1px solid" borderColor={borderColor}>
                    <ModalHeader
                        bg={sectionHeaderBg}
                        color={textColor}
                        textAlign="center"
                        py={5}
                    >
                        <VStack spacing={2}>
                            <Box
                                p={2}
                                bg={bgColor}
                                borderRadius="md"
                            >
                                <FaPaperclip size={22} color={accentColor} />
                            </Box>
                            <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">إضافة مرفق</Text>
                        </VStack>
                    </ModalHeader>
                    <ModalCloseButton color={textColor} />

                    <ModalBody p={{ base: 4, md: 8 }}>
                        <VStack spacing={5}>
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="semibold">اسم المرفق</FormLabel>
                                <Input
                                    value={AttchmentName}
                                    onChange={(e) => setAttchmentName(e.target.value)}
                                    placeholder="أدخل اسم المرفق"
                                    borderRadius="md"
                                    borderWidth="1.5px"
                                    borderColor={borderColor}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={textColor} fontWeight="semibold">ملف المرفق</FormLabel>
                                <Input
                                    type="file"
                                    onChange={(e) => setAttachments(e.target.files[0])}
                                    borderRadius="md"
                                    borderWidth="1.5px"
                                    borderColor={borderColor}
                                    p={2}
                                />
                            </FormControl>
                        </VStack>
                    </ModalBody>

                    <ModalFooter p={{ base: 4, md: 8 }} pt={0}>
                        <Stack
                            direction={{ base: "column", sm: "row" }}
                            spacing={3}
                            w="full"
                        >
                            <Button
                                variant="outline"
                                onClick={() => setAttachmentModalOpen(false)}
                                borderRadius="md"
                                borderWidth="1.5px"
                                px={6}
                                w={{ base: "full", sm: "auto" }}
                                _hover={{
                                    bg: sectionHeaderHoverBg
                                }}
                            >
                                إلغاء
                            </Button>
                            <Button
                                colorScheme="blue"
                                color="white"
                                onClick={() => saveAttachmentsToPartie(selectedChapterIndex, selectedPartieIndex, attachments)}
                                isDisabled={!attachments || !AttchmentName}
                                borderRadius="md"
                                px={6}
                                w={{ base: "full", sm: "auto" }}
                                _hover={{
                                    bg: accentColor
                                }}
                                transition="all 0.2s"
                            >
                                رفع المرفق
                            </Button>
                        </Stack>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* New Chapter Modal */}
            <NewChapterToCourse
                isOpen={isOpen}
                onClose={onClose}
                courseId={id}
                onChapterAdded={fetchCourseData}
            />

            {/* New Partie Modal */}
            <AddPartieForm
                isOpen={isAddNewPartieOpen}
                onClose={() => setisAddNewPartieOpen(false)}
                courseId={id}
                chapterId={ChaterID}
                onPartieAdded={fetchCourseData}
            />
        </>
    );
};

export default CourseDetailsContent;
